/**
 * Wires `revisions.model.ts` to the three revision doors on one consignment.
 *
 * `fetchConsignmentRevisions` for both halves — the terms in force and every
 * version proposed — and `proposeConsignmentRevision` and
 * `answerConsignmentRevision` to change them, each followed by a RE-READ rather
 * than a patch. That is not caution: an acceptance may be the one that puts the
 * terms in force, which changes `terms` as well as the revision, and only
 * Postgres knows whether it was the last one needed.
 *
 * The state the model cannot hold is the form — open, draft, problem, sending,
 * sent — the answer in flight, the error, and the reload counter.
 */

import { useCallback, useEffect, useState } from "react";
import type { ConsignmentRevisionsResponse } from "@ondo/shared";

import {
  answerConsignmentRevision,
  fetchConsignmentRevisions,
  proposeConsignmentRevision,
} from "../lib/api.ts";
import {
  describeRevisionError,
  draftFrom,
  toProposeRequest,
  toRevisionsView,
} from "./revisions.model.ts";
import type { RevisionDraft, RevisionsView } from "./revisions.model.ts";

export interface RevisionsController {
  view: RevisionsView;
  /** The proposal form: open, typed, refused, sending, sent. */
  form: {
    open: boolean;
    draft: RevisionDraft;
    problem: string | null;
    sending: boolean;
    sent: boolean;
  };
  openForm: () => void;
  closeForm: () => void;
  setDraft: (patch: Partial<RevisionDraft>) => void;
  propose: () => void;
  /** Answering the pending version. `accept: false` refuses it and ends nothing else. */
  answer: (accept: boolean) => void;
  /** Which answer is in flight, so the view can disable the pair. */
  answering: "accept" | "refuse" | null;
  error: string | null;
}

const BLANK: RevisionDraft = {
  basis: "unspecified",
  currency: "GBP",
  asking: "",
  floor: "",
  note: "",
};

/**
 * One consignment's terms, from either party's side.
 *
 * @param canAct whether the reader may propose at all. Decides only what is
 *   drawn. Whether their own side owes an ANSWER is a different question and is
 *   not decided here: it comes back on the row as `yoursToAnswer`, resolved from
 *   the reader's memberships in SQL, because a party may be a firm they work
 *   for and the browser cannot ask about memberships.
 */
export function useRevisionsController(
  consignmentId: string,
  canAct: boolean,
): RevisionsController {
  const [response, setResponse] = useState<ConsignmentRevisionsResponse | undefined>(
    undefined,
  );
  const [open, setOpen] = useState(false);
  const [draft, setDraftState] = useState<RevisionDraft>(BLANK);
  const [problem, setProblem] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [answering, setAnswering] = useState<"accept" | "refuse" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let active = true;

    fetchConsignmentRevisions(consignmentId)
      .then((found) => {
        if (active) setResponse(found);
      })
      // A refusal is the ordinary case for a reader who may see the work but is
      // not a party to the arrangement. The panel then says the terms are not
      // open to them, which is what they may know.
      .catch(() => {
        if (active) setResponse({ terms: null, revisions: [] });
      });

    return () => {
      active = false;
    };
  }, [consignmentId, nonce]);

  const setDraft = useCallback((patch: Partial<RevisionDraft>) => {
    setDraftState((current) => ({ ...current, ...patch }));
    setProblem(null);
  }, []);

  const view = toRevisionsView(response, canAct);

  return {
    view,
    form: { open, draft, problem, sending, sent },
    answering,
    error,

    openForm: () => {
      // PREFILLED FROM WHAT IS IN FORCE, because the database takes a complete
      // set of terms and has no partial update. A blank form would make
      // "change the floor" silently mean "and agree no asking price".
      setDraftState(response?.terms ? draftFrom(response.terms) : BLANK);
      setOpen(true);
      setSent(false);
      setProblem(null);
      setError(null);
    },
    closeForm: () => {
      setOpen(false);
      setProblem(null);
    },
    setDraft,

    propose: () => {
      if (!response?.terms) return;

      const parsed = toProposeRequest(draft, response.terms);
      if (!parsed.ok) {
        setProblem(parsed.problem);
        return;
      }

      setSending(true);
      setError(null);

      proposeConsignmentRevision(consignmentId, parsed.body)
        .then(() => {
          setSent(true);
          setOpen(false);
          setNonce((n) => n + 1);
        })
        .catch((cause: unknown) => setError(describeRevisionError(cause)))
        .finally(() => setSending(false));
    },

    answer: (accept: boolean) => {
      if (view.kind !== "terms" || !view.pending) return;

      setAnswering(accept ? "accept" : "refuse");
      setError(null);

      answerConsignmentRevision(consignmentId, view.pending.revisionId, accept)
        .then(() => {
          // RE-READ RATHER THAN PATCHED, and on an acceptance this is the only
          // way to know what happened: if it was the last answer needed, the
          // terms in force have changed in the same transaction. The row cannot
          // tell the screen that; the re-read can.
          setSent(false);
          setNonce((n) => n + 1);
        })
        .catch((cause: unknown) => setError(describeRevisionError(cause)))
        .finally(() => setAnswering(null));
    },
  };
}
